<template>
  <!-- FT-217 REQ-05 / REQ-09: предложение переноса над таблицей суток -->
  <v-alert v-if="variant === 'suggestion'" v-show="suggestion" type="warning" text dense outlined
    class="mb-2" data-testid="price-group-suggestion">
    <div>{{ suggestionText }}</div>
    <div class="d-flex flex-wrap mt-2" style="gap: 8px">
      <v-btn v-if="suggestion && suggestion.kind === 'move'" small color="primary" :loading="busy === 'move'"
        :disabled="!!busy" data-testid="price-group-move" @click="send('move', { price_group_id: suggestion.target_group_id })">
        {{ $t("price_groups.move") }}
      </v-btn>
      <v-btn small outlined :loading="busy === 'new'" :disabled="!!busy" data-testid="price-group-new"
        @click="send('new', { new_group: true })">
        {{ $t("price_groups.to_new_group") }}
      </v-btn>
      <v-btn small text :loading="busy === 'ack'" :disabled="!!busy" data-testid="price-group-ack"
        @click="send('ack', { ack: true })">
        {{ $t("price_groups.keep") }}
      </v-btn>
    </div>
  </v-alert>
  <!-- FT-217 REQ-09: ручной выбор группы рядом с почасовой ценой -->
  <v-select v-else :key="selectKey" :value="selected" :items="items" :label="$t('price_groups.group_label')"
    :placeholder="$t('price_groups.no_group')" :loading="busy === 'select'" :disabled="!!busy || !isStaff"
    dense outlined hide-details class="my-2" data-testid="price-group-select" @change="onSelect" />
</template>

<script>
import { priceGroupLabel, priceGroupLabelOptions, sortByMinPrice } from "@/utils/priceGroupLabel";
import { fetchPriceGroups, patchCarPriceGroup, priceGroupErrorCode } from "@/utils/priceGroupsApi";

// Значение пункта «Новая группа» в v-select (id групп — числа, коллизии нет).
export const NEW_GROUP_VALUE = "__new__";

// Ровно одно состояние на оба места вкладки «Цены»: его держит CarCard и отдаёт
// обоим экземплярам (выбор и предложение) пропом state; после ответа сервера
// компонент шлёт новое состояние событием update:state.
//   state = { price_group: { id, min, max } | null, suggestion, groups: [{ id, min, max }], unit }
export default {
  name: "PriceGroupPicker",
  props: {
    carId: { type: [Number, String], required: true },
    state: { type: Object, required: true },
    variant: { type: String, default: "select" }, // "select" | "suggestion"
  },
  data() {
    // selectKey — пересоздать v-select после отказа: при :value без изменения пропа
    // он остался бы показывать невыбранный сервером пункт
    return { busy: null, selectKey: 0 };
  },
  computed: {
    labelOptions() {
      return priceGroupLabelOptions(this, this.state.unit);
    },
    selected() {
      return this.state.price_group ? this.state.price_group.id : null;
    },
    items() {
      return [
        ...sortByMinPrice(this.state.groups).map((g) => ({ text: priceGroupLabel(g, this.labelOptions), value: g.id })),
        { text: this.$t("price_groups.new_group"), value: NEW_GROUP_VALUE },
      ];
    },
    suggestion() {
      return this.state.suggestion || null;
    },
    // «Цена ушла от группы на +52 %. Перенести в 4 500–4 900 ₽?» (SC-04);
    // deviation — доля «цена / медиана других − 1»
    suggestionText() {
      const s = this.suggestion;
      if (!s) return "";
      const pct = this.formatDeviation(s.deviation);
      if (s.kind === "move") {
        const label = priceGroupLabel({ min: s.target_min, max: s.target_max }, this.labelOptions);
        return this.$t("price_groups.suggestion_move", { pct, label });
      }
      return this.$t("price_groups.suggestion_new", { pct });
    },
  },
  methods: {
    formatDeviation(value) {
      const n = Number(value) || 0;
      const locale = (this.$i18n && this.$i18n.locale) || undefined;
      try {
        return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0, signDisplay: "always" }).format(n);
      } catch (e) {
        return `${n > 0 ? "+" : ""}${Math.round(n * 100)}%`;
      }
    },
    onSelect(value) {
      if (value === this.selected) return;
      // Новую группу показываем только по ответу сервера: v-select сам уже выставил
      // выбранный пункт — пересоздаём его на прежнем значении (с лоадером), а
      // update:state после ответа покажет новую группу; отказ ничего не меняет.
      this.selectKey += 1;
      if (value === NEW_GROUP_VALUE) this.send("select", { new_group: true });
      else if (value != null) this.send("select", { price_group_id: value });
    },
    async send(kind, body) {
      if (this.busy) return;
      this.busy = kind;
      try {
        const res = await patchCarPriceGroup(this, this.carId, body);
        const id = res.price_group_id;
        // Перенос мог создать группу или опустошить прежнюю (её сервер архивирует) —
        // список и границы групп перечитываем; не вышло — остаёмся на прежнем списке.
        let groups = this.state.groups || [];
        let unit = this.state.unit;
        if (kind !== "ack") {
          try {
            const fresh = await fetchPriceGroups(this);
            groups = (fresh.groups || []).map((g) => ({ id: g.id, min: g.min, max: g.max }));
            unit = fresh.unit || unit;
          } catch (e) {
            console.log(e);
          }
        }
        const current = groups.find((g) => g.id === id);
        this.$emit("update:state", {
          ...this.state,
          groups,
          unit,
          price_group: id == null ? null : current || { id, min: null, max: null },
          suggestion: res.suggestion || null,
        });
        this.$notify && this.$notify(this.$t("custom.updated"), "", "success");
      } catch (error) {
        // no_price: у машины нет цены в единице филиала — общий текст errors.codes.no_price
        // про тип аренды здесь не подходит
        if (priceGroupErrorCode(error) === "no_price") {
          this.$notify && this.$notify(this.$t("price_groups.no_price_error"), "", "warning", 6000);
        } else {
          this.setError && this.setError(error, this.$t("price_groups.save_error"));
        }
        this.selectKey += 1;
      } finally {
        this.busy = null;
      }
    },
  },
};
</script>
